import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { ShieldAlert, Clock, PencilLine, Trash2, Send } from 'lucide-react';
import { agentCommunicationExceptionAPI } from '@/lib/api';
import { isKRRole, isAgencyPrincipal, isAgent, formatDate, getErrorMessage } from '@/lib/utils';

const POLICY_LABELS = {
  direct_contact_allowed: 'Allow Direct Contact',
  agency_coordination_required: 'Require Agency Coordination',
  restricted_direct_contact: 'Restricted Direct Contact',
};
const POLICY_DESCRIPTIONS = {
  direct_contact_allowed:
    'KR Enterprise Group may communicate directly with this agent, regardless of the agency policy.',
  agency_coordination_required:
    'Communication with this agent should be coordinated through their agency, regardless of the agency policy.',
  restricted_direct_contact:
    'Direct communication with this agent requires explicit authorization.',
};
const POLICY_TONE = {
  direct_contact_allowed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  agency_coordination_required: 'bg-amber-100 text-amber-700 border-amber-200',
  restricted_direct_contact: 'bg-rose-100 text-rose-700 border-rose-200',
};
const SOURCE_LABELS = {
  agent_override: 'Agent Exception',
  agent_exception: 'Agent Exception',
  agency_default: 'Agency Policy',
  platform_default: 'Platform Default',
};

const PolicyBadge = ({ value }) => (
  <Badge
    className={`text-xs border ${POLICY_TONE[value] || 'bg-slate-100 text-slate-700 border-slate-200'}`}
  >
    {POLICY_LABELS[value] || value || '—'}
  </Badge>
);

export const AgentCommunicationException = ({ agent }) => {
  const { user } = useAuth();
  const npn = agent?.npn;
  const isKR = isKRRole(user);
  const isSelfAgent = isAgent(user) && user?.agent_npn === npn;
  const isPrincipal = isAgencyPrincipal(user);
  const canRequest = isSelfAgent || isPrincipal;

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setDialogOpen, setSetDialogOpen] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!npn) return;
    setLoading(true);
    try {
      const res = await agentCommunicationExceptionAPI.get(npn);
      setState(res.data);
    } catch (e) {
      console.error('Failed to load agent communication exception:', e);
    } finally {
      setLoading(false);
    }
  }, [npn]);

  useEffect(() => { load(); }, [load]);

  const handleSet = async (payload) => {
    setSubmitting(true);
    try {
      await agentCommunicationExceptionAPI.set(npn, payload);
      toast.success('Communication exception saved');
      setSetDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to save exception'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequest = async (payload) => {
    setSubmitting(true);
    try {
      await agentCommunicationExceptionAPI.request(npn, payload);
      toast.success('Exception request submitted — pending KR review');
      setRequestDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to submit request'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async () => {
    setSubmitting(true);
    try {
      await agentCommunicationExceptionAPI.remove(npn);
      toast.success('Communication exception removed');
      setRemoveDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to remove exception'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!npn) return null;
  if (!isKR && !canRequest) return null;

  const effectivePolicy = state?.effective_policy;
  const effectiveSource = state?.effective_source;
  const hasException = !!state?.has_exception;
  const pending = state?.pending_change;

  return (
    <Card data-testid="agent-communication-exception-block">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ShieldAlert size={16} className="text-slate-500" />
            <CardTitle className="text-sm font-semibold text-slate-900">
              Communication Exception
            </CardTitle>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isKR && !hasException && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSetDialogOpen(true)}
                data-testid="agent-exception-set-btn"
              >
                <PencilLine size={14} className="mr-1" /> Set Exception
              </Button>
            )}
            {isKR && hasException && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSetDialogOpen(true)}
                  data-testid="agent-exception-edit-btn"
                >
                  <PencilLine size={14} className="mr-1" /> Update
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setRemoveDialogOpen(true)}
                  data-testid="agent-exception-remove-btn"
                >
                  <Trash2 size={14} className="mr-1" /> Remove
                </Button>
              </>
            )}
            {!isKR && canRequest && !pending && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRequestDialogOpen(true)}
                data-testid="agent-exception-request-btn"
              >
                <Send size={14} className="mr-1" /> Request Exception
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <p className="text-sm text-slate-500" data-testid="agent-exception-loading">Loading exception…</p>
        ) : (
          <>
            <div
              className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
              data-testid="agent-exception-effective"
            >
              <span className="text-xs uppercase tracking-wider text-slate-500">Effective Communication Status</span>
              <PolicyBadge value={effectivePolicy} />
              <span className="text-xs text-slate-500">
                Source: <span className="font-medium text-slate-700">{SOURCE_LABELS[effectiveSource] || effectiveSource || '—'}</span>
              </span>
            </div>

            {hasException && state?.exception && (
              <div
                className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm space-y-1"
                data-testid="agent-exception-details"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs uppercase tracking-wider text-slate-500">Agent-Level Exception</span>
                  <PolicyBadge value={state.exception.policy} />
                </div>
                {state.exception.reason && (
                  <p className="text-xs text-slate-600">
                    <span className="font-semibold">Reason:</span> {state.exception.reason}
                  </p>
                )}
                <p className="text-xs text-slate-500">
                  Set by {state.exception.set_by || 'unknown'} · {state.exception.set_at ? formatDate(state.exception.set_at) : ''}
                  {state.exception.expires_at ? ` · Expires ${formatDate(state.exception.expires_at)}` : ''}
                </p>
              </div>
            )}

            {!hasException && (
              <p className="text-xs text-slate-500" data-testid="agent-exception-none">
                No agent-level exception is set. The agent inherits the {SOURCE_LABELS[effectiveSource] || 'default'}.
              </p>
            )}

            {pending && (
              <div
                className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-1"
                data-testid="agent-exception-pending"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <Clock size={14} />
                  <span className="font-semibold">Pending request:</span>
                  <PolicyBadge value={pending.proposed_value} />
                </div>
                <p>
                  Requested by {pending.proposed_by_user_email || 'unknown'}
                  {pending.proposed_at ? ` on ${formatDate(pending.proposed_at)}` : ''}
                  {pending.proposed_expires_at ? ` · Expires ${formatDate(pending.proposed_expires_at)}` : ''}
                </p>
                {pending.proposed_reason && (
                  <p><span className="font-semibold">Reason:</span> {pending.proposed_reason}</p>
                )}
                {isKR && (
                  <p className="text-amber-900 italic">
                    Review in the Communication Governance inbox to approve or reject.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>

      <ExceptionFormDialog
        open={setDialogOpen}
        onOpenChange={setSetDialogOpen}
        mode="kr"
        current={state?.exception}
        submitting={submitting}
        onSubmit={handleSet}
      />
      <ExceptionFormDialog
        open={requestDialogOpen}
        onOpenChange={setRequestDialogOpen}
        mode="request"
        current={null}
        submitting={submitting}
        onSubmit={handleRequest}
      />

      <Dialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <DialogContent data-testid="agent-exception-remove-dialog">
          <DialogHeader>
            <DialogTitle>Remove Communication Exception</DialogTitle>
            <DialogDescription>
              The agent will revert to their agency policy. This action is recorded in the audit log.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemove}
              disabled={submitting}
              data-testid="agent-exception-remove-confirm-btn"
            >
              {submitting ? 'Removing…' : 'Remove Exception'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const ExceptionFormDialog = ({ open, onOpenChange, mode, current, submitting, onSubmit }) => {
  // mode: 'kr' (immediate save) | 'request' (creates pending)
  const [policy, setPolicy] = useState(null);
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  useEffect(() => {
    if (open) {
      setPolicy(current?.policy || 'direct_contact_allowed');
      setReason(current?.reason || '');
      setExpiresAt(current?.expires_at ? current.expires_at.slice(0, 10) : '');
    }
  }, [open, current]);

  const submit = () => {
    if (!policy) return;
    if (mode === 'request' && !reason.trim()) return;
    onSubmit({
      policy,
      reason: reason.trim() || null,
      expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
    });
  };

  const title = mode === 'kr' ? 'Set Communication Exception' : 'Request Communication Exception';
  const submitLabel = mode === 'kr' ? 'Save Exception' : 'Submit Request';
  const canSubmit = !!policy && (mode === 'kr' || reason.trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid={mode === 'kr' ? 'agent-exception-set-dialog' : 'agent-exception-request-dialog'}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {mode === 'kr'
              ? 'Overrides the agency policy for this agent only. Takes effect immediately.'
              : 'Submits a request to KR — no change takes effect until it is approved.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <RadioGroup
            value={policy || ''}
            onValueChange={setPolicy}
            className="space-y-2"
            data-testid="agent-exception-policy-radio"
          >
            {Object.keys(POLICY_LABELS).map((key) => (
              <label
                key={key}
                className="flex items-start gap-2 rounded-md border border-slate-200 bg-white p-2 cursor-pointer hover:border-slate-300 transition-colors"
                data-testid={`agent-exception-option-${key}`}
              >
                <RadioGroupItem value={key} id={`exception-${key}`} className="mt-0.5" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900">{POLICY_LABELS[key]}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{POLICY_DESCRIPTIONS[key]}</div>
                </div>
              </label>
            ))}
          </RadioGroup>

          <div className="space-y-1">
            <Label className="text-xs">
              Reason {mode === 'request' ? '(required)' : '(optional)'}
            </Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder={mode === 'kr'
                ? 'Context for the audit log — why this agent needs an exception.'
                : 'Explain why this agent needs a communication exception.'}
              data-testid="agent-exception-reason-input"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Expiration (optional)</Label>
            <Input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              data-testid="agent-exception-expires-input"
            />
            <p className="text-[11px] text-slate-500">
              After this date the exception falls back to the agency policy.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit || submitting}
            data-testid={mode === 'kr' ? 'agent-exception-save-confirm-btn' : 'agent-exception-request-confirm-btn'}
          >
            {submitting ? 'Submitting…' : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AgentCommunicationException;
